// src/scripts/calendar/utils/AdapterErrorManager.js
/**
 * AdapterErrorManager - Error handling and recovery for adapter components
 *
 * Responsibilities:
 * - Manage error handling and recovery for adapter components
 * - Coordinate with StateManager for error state transitions
 * - Provide error tracking and debugging capabilities
 * - Essential error tracking for critical error management failures
 *
 * Key Methods:
 * - handleError() - Error processing with state management (ALWAYS THROWS)
 * - clearError() - Error state cleanup
 * - getLastError() - Error retrieval for debugging
 * - getRecentErrors() - Recent failure debugging
 * - hasCriticalIssues() - Quick error status check
 * - clearErrorHistory() - Reset error tracking
 *
 * Error Handling Semantics:
 * - handleError() ALWAYS throws after processing - this is by design
 * - All thrown values are normalized to proper Error instances
 * - Original errors are preserved via Error.cause when wrapping
 *
 * Error Handling:
 * - configuration: Error handler and state manager availability failures
 * - initialization: Error management setup failures
 *
 * Note: Ultra-minimal error tracking approach - focuses only on critical failures
 * that could break error management functionality itself.
 */

import { AdapterState } from './StateManager.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

// Essential error tracking setup (ultra-minimal)
const errorHandler = new ErrorHandler({
  module: 'AdapterErrorManager',
  maxErrors: 5
});

/**
 * Normalize any value to a proper Error instance
 * Ensures consistent error handling regardless of what was thrown
 *
 * @param {*} value - The value to normalize (Error, string, object, etc.)
 * @param {string} fallbackMessage - Message to use if value has no message
 * @returns {Error} A proper Error instance
 * @private
 */
function normalizeToError(value, fallbackMessage = 'Unknown error') {
  // Already an Error instance - return as-is
  if (value instanceof Error) {
    return value;
  }

  // String - wrap in Error
  if (typeof value === 'string') {
    return new Error(value);
  }

  // Object with message property - create Error with that message
  if (value && typeof value === 'object' && value.message) {
    const error = new Error(value.message);
    // Copy any additional properties from the object
    Object.assign(error, value);
    return error;
  }

  // Null/undefined or other - create Error with fallback message
  if (value == null) {
    return new Error(fallbackMessage);
  }

  // Anything else - try to stringify it
  try {
    return new Error(String(value));
  } catch {
    return new Error(fallbackMessage);
  }
}

/**
 * Manages error handling and recovery
 */
export default class AdapterErrorManager {
  constructor(externalErrorHandler, stateManager) {
    this.errorHandler = externalErrorHandler;
    this.stateManager = stateManager;
    this.lastError = null;

    // Log warnings for missing dependencies (non-fatal)
    if (!externalErrorHandler) {
      errorHandler.handleError('External error handler not provided to AdapterErrorManager', null, {
        category: 'configuration',
        action: 'missing-error-handler'
      });
    }

    if (!stateManager) {
      errorHandler.handleError('State manager not provided to AdapterErrorManager', null, {
        category: 'configuration',
        action: 'missing-state-manager'
      });
    }

    errorHandler.logSuccess('AdapterErrorManager initialized successfully', {
      category: 'initialization',
      action: 'init-success',
      hasErrorHandler: !!externalErrorHandler,
      hasStateManager: !!stateManager
    });
  }

  /**
   * Handle an error with state management coordination
   *
   * IMPORTANT: This method ALWAYS throws after processing.
   * This is intentional - it processes the error, records it,
   * transitions state, then re-throws for the caller to handle.
   *
   * @param {string} message - Human-readable error description
   * @param {*} error - The error value (will be normalized to Error instance)
   * @param {string} category - Error category for classification
   * @throws {Error} Always throws - either the normalized error or wrapped error
   */
  handleError(message, error, category) {
    // Normalize the error to a proper Error instance
    const normalizedError = normalizeToError(error, message);

    // Record the error for debugging
    this.lastError = {
      message,
      error: normalizedError,
      category,
      timestamp: Date.now()
    };

    // Delegate to external error handler if available
    if (this.errorHandler && typeof this.errorHandler.handleError === 'function') {
      this.errorHandler.handleError(message, normalizedError, { category });
    } else {
      // Log to internal error handler if external not available
      errorHandler.handleError('External error handler not available for error processing', null, {
        category: 'configuration',
        action: 'error-handler-unavailable',
        originalMessage: message
      });
    }

    // Transition to error state if possible
    this._transitionToErrorState(message, category);

    // ALWAYS throw - this is the documented contract
    // If original error had useful context, preserve it via cause
    if (normalizedError.message !== message) {
      throw new Error(message, { cause: normalizedError });
    }
    throw normalizedError;
  }

  /**
   * Attempt to transition state manager to ERROR state
   * Fails silently if transition is not valid (e.g., already in ERROR state)
   *
   * @param {string} errorMessage - Error message for metadata
   * @param {string} category - Error category for metadata
   * @private
   */
  _transitionToErrorState(errorMessage, category) {
    if (!this.stateManager) return;

    try {
      if (typeof this.stateManager.canTransitionTo === 'function' &&
          this.stateManager.canTransitionTo(AdapterState.ERROR)) {
        this.stateManager.transitionTo(AdapterState.ERROR, {
          error: errorMessage,
          category
        });
      }
      // If can't transition (e.g., already in ERROR), that's fine - continue silently
    } catch (transitionError) {
      // State transition failed - log but don't interrupt error handling
      errorHandler.handleError('Failed to transition to error state', transitionError, {
        category: 'configuration',
        action: 'state-transition-failed',
        targetState: AdapterState.ERROR
      });
    }
  }

  /**
   * Clear the last recorded error
   */
  clearError() {
    this.lastError = null;
  }

  /**
   * Get the last recorded error
   * @returns {Object|null} Last error record or null
   */
  getLastError() {
    return this.lastError;
  }

  /**
   * Get recent errors for debugging
   * @param {number} minutes - Minutes to look back (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(minutes = 5) {
    return errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues that need attention
   * @returns {boolean} True if critical issues exist
   */
  hasCriticalIssues() {
    return errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    errorHandler.clearErrorHistory();
  }

  /**
   * Get comprehensive stats including error information
   * @returns {Object} Stats object with error data
   */
  getStats() {
    const errorStats = errorHandler.getErrorStats();

    return {
      hasErrorHandler: !!this.errorHandler,
      hasStateManager: !!this.stateManager,
      lastError: this.lastError,
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }
}
